import { fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';
import { dotenv } from '@/utils/constants/env';
import { refreshSession, clearSession } from '@/redux/slices/sessionStateSlice';
import type { RootState } from './store';

const mutex = new Mutex();

const rawBaseQuery = fetchBaseQuery({
  baseUrl: dotenv.API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).sessionState.accessToken;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

/** True if the JWT's exp claim is already past (or within 30 s of expiring). */
function isTokenExpired(token: string | null | undefined): boolean {
  if (!token) return false; // no token → not our problem to refresh proactively
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return Date.now() >= (payload.exp - 30) * 1000;
  } catch {
    return false; // malformed JWT — let the server decide
  }
}

type RefreshResponseData = {
  access_token: string;
  refresh_token?: string;
  roles?: string[];
  permissions?: string[];
  subscription_status?: string;
};

/** Calls /auth/refresh and updates Redux. Returns true on success. */
async function attemptRefresh(
  api: Parameters<BaseQueryFn>[1],
  extraOptions: Parameters<BaseQueryFn>[2]
): Promise<boolean> {
  const state = api.getState() as RootState;
  const refreshToken = state.sessionState.refreshToken;
  if (!refreshToken) {
    api.dispatch(clearSession());
    return false;
  }
  const refreshResult = await rawBaseQuery(
    { url: '/api/v1/auth/refresh', method: 'POST', body: { refresh_token: refreshToken } },
    api,
    extraOptions
  );
  if (refreshResult.error) {
    const errData = (refreshResult.error as { data?: { message_code?: number } })?.data;
    const code = errData?.message_code;
    api.dispatch(clearSession());
    if (code === 1121) window.location.href = '/suspended/tenant';
    else if (code === 1120 || code === 1007) window.location.href = '/suspended/account';
    return false;
  }
  if (refreshResult.data) {
    const { data } = refreshResult.data as { data: RefreshResponseData };
    api.dispatch(
      refreshSession({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        roles: data.roles,
        permissions: data.permissions,
        subscription_status: data.subscription_status,
      })
    );
    return true;
  }
  api.dispatch(clearSession());
  return false;
}

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // Skip re-auth logic entirely for the auth endpoints themselves.
  const reqUrl = typeof args === 'string' ? args : (args as FetchArgs).url ?? '';
  const isAuthEndpoint =
    reqUrl.includes('/auth/login') ||
    reqUrl.includes('/auth/google') ||
    reqUrl.includes('/auth/register');

  if (!isAuthEndpoint) {
    await mutex.waitForUnlock();

    // Proactive refresh: if the stored token is already expired, refresh before the
    // request fires so we never send a request we know will 401.
    if (!mutex.isLocked()) {
      const { accessToken } = (api.getState() as RootState).sessionState;
      if (isTokenExpired(accessToken)) {
        const release = await mutex.acquire();
        try {
          // Re-check after acquiring in case a concurrent request already refreshed.
          if (isTokenExpired((api.getState() as RootState).sessionState.accessToken)) {
            await attemptRefresh(api, extraOptions);
          }
        } finally {
          release();
        }
        await mutex.waitForUnlock();
      }
    }
  }

  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401 && !isAuthEndpoint) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshed = await attemptRefresh(api, extraOptions);
        if (refreshed) {
          result = await rawBaseQuery(args, api, extraOptions);
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await rawBaseQuery(args, api, extraOptions);
    }
  }
  return result;
};
